/**
 * Integration Test UI — EcoleMinds API
 *
 * Run locally to verify frontend-to-backend connectivity.
 * Navigate to: /dev/integration-test
 *
 * This page is available in all environments but is primarily intended
 * for local development and staging verification.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Play,
  RotateCcw,
  CheckCircle,
  XCircle,
  SkipForward,
  Circle,
  ChevronDown,
  ChevronRight,
  Clock,
  Server,
  ShieldAlert,
  AlertTriangle,
  Info,
  Copy,
} from 'lucide-react';
import { allTests, testGroups, type TestCase, type TestResult, type TestStatus } from './apiTests';
import { API_CONFIG } from '../../utils/constants';

const API_BASE = API_CONFIG.BASE_URL;

const statusConfig: Record<
  TestStatus,
  { icon: React.ReactNode; color: string; bg: string; label: string }
> = {
  idle: { icon: <Circle className="w-4 h-4" />, color: 'text-slate-400', bg: 'bg-slate-50', label: 'Idle' },
  running: { icon: <Clock className="w-4 h-4 animate-spin" />, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Running' },
  passed: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Passed' },
  failed: { icon: <XCircle className="w-4 h-4" />, color: 'text-red-500', bg: 'bg-red-50', label: 'Failed' },
  skipped: { icon: <SkipForward className="w-4 h-4" />, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Skipped' },
};

function getToken(): string | null {
  return localStorage.getItem('ecm_token');
}

function getUserRole(): string | null {
  try {
    const token = getToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || null;
  } catch {
    return null;
  }
}

function isDevMode(): boolean {
  return (import.meta as any).env.DEV === true || (import.meta as any).env.VITE_APP_ENV === 'development';
}

export default function IntegrationTestScreen(): React.JSX.Element {
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(testGroups));
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<TestStatus | 'all'>('all');
  const [authToken, setAuthToken] = useState<string | null>(getToken());
  const [userRole, setUserRole] = useState<string | null>(getUserRole());

  useEffect(() => {
    const interval = setInterval(() => {
      setAuthToken(getToken());
      setUserRole(getUserRole());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const toggleTest = (id: string) => {
    setExpandedTests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runTest = useCallback(async (test: TestCase): Promise<TestResult> => {
    const start = performance.now();

    if (test.skip) {
      return {
        id: test.id,
        status: 'skipped',
        durationMs: 0,
        error: test.skipReason || 'Skipped by default',
      };
    }

    // Auth gate checks
    if (test.requiresAuth && !authToken) {
      return {
        id: test.id,
        status: 'skipped',
        durationMs: 0,
        error: 'No auth token available. Log in first.',
      };
    }
    if (test.requiresAdmin && userRole !== 'admin') {
      return {
        id: test.id,
        status: 'skipped',
        durationMs: 0,
        error: `Requires admin role. Current role: ${userRole || 'none'}.`,
      };
    }
    if (test.requiresTrainer && !['admin', 'trainer'].includes(userRole || '')) {
      return {
        id: test.id,
        status: 'skipped',
        durationMs: 0,
        error: `Requires trainer role. Current role: ${userRole || 'none'}.`,
      };
    }

    try {
      await test.run();
      return {
        id: test.id,
        status: 'passed',
        durationMs: Math.round(performance.now() - start),
      };
    } catch (error: any) {
      return {
        id: test.id,
        status: 'failed',
        durationMs: Math.round(performance.now() - start),
        error: error?.message || String(error) || 'Unknown error',
      };
    }
  }, [authToken, userRole]);

  const runAll = useCallback(async () => {
    setIsRunning(true);
    setResults({});
    const newResults: Record<string, TestResult> = {};

    for (const test of allTests) {
      setResults((prev) => ({ ...prev, [test.id]: { id: test.id, status: 'running' } }));
      const result = await runTest(test);
      newResults[test.id] = result;
      setResults((prev) => ({ ...prev, [test.id]: result }));
    }

    setIsRunning(false);
  }, [runTest]);

  const runGroup = useCallback(
    async (group: string) => {
      setIsRunning(true);
      const groupTests = allTests.filter((t) => t.group === group);
      for (const test of groupTests) {
        setResults((prev) => ({ ...prev, [test.id]: { id: test.id, status: 'running' } }));
        const result = await runTest(test);
        setResults((prev) => ({ ...prev, [test.id]: result }));
      }
      setIsRunning(false);
    },
    [runTest],
  );

  const reset = () => {
    setResults({});
    setExpandedTests(new Set());
  };

  const copyResults = () => {
    const lines = allTests.map((t) => {
      const r = results[t.id];
      if (!r) return `${t.name} — not run`;
      return `${t.name} — ${r.status}${r.error ? ` | ${r.error}` : ''}${r.durationMs ? ` | ${r.durationMs}ms` : ''}`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  const stats = useMemo(() => {
    const vals = Object.values(results);
    return {
      total: allTests.length,
      run: vals.length,
      passed: vals.filter((r) => r.status === 'passed').length,
      failed: vals.filter((r) => r.status === 'failed').length,
      skipped: vals.filter((r) => r.status === 'skipped').length,
      running: vals.filter((r) => r.status === 'running').length,
    };
  }, [results]);

  const grouped = useMemo(() => {
    const map = new Map<string, TestCase[]>();
    for (const group of testGroups) {
      map.set(
        group,
        allTests.filter((t) => t.group === group),
      );
    }
    return map;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-600" />
                Integration Tests
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Backend: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{API_BASE}</code>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Auth pill */}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${authToken ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {authToken ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                {authToken ? `Auth (${userRole || 'user'})` : 'No auth token'}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-medium">{stats.passed} passed</span>
                <span className="px-2 py-1 rounded-md bg-red-50 text-red-700 font-medium">{stats.failed} failed</span>
                <span className="px-2 py-1 rounded-md bg-amber-50 text-amber-700 font-medium">{stats.skipped} skipped</span>
                <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-medium">{stats.running} running</span>
              </div>

              {/* Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as TestStatus | 'all')}
                className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
                <option value="running">Running</option>
                <option value="idle">Idle</option>
              </select>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={runAll}
                  disabled={isRunning}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Play className="w-4 h-4" />
                  {isRunning ? 'Running…' : 'Run All'}
                </button>
                <button
                  onClick={reset}
                  disabled={isRunning}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  onClick={copyResults}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                  title="Copy results to clipboard"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {!isDevMode() && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Production mode detected.</strong> Some tests may modify data or require specific permissions.
                Run integration tests primarily in local/staging environments.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tests */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {Array.from(grouped.entries()).map(([group, tests]) => {
          const visibleTests = tests.filter((t) => {
            const r = results[t.id];
            if (filter === 'all') return true;
            return (r?.status || 'idle') === filter;
          });

          if (visibleTests.length === 0) return null;

          const groupResultCounts = {
            passed: tests.filter((t) => results[t.id]?.status === 'passed').length,
            failed: tests.filter((t) => results[t.id]?.status === 'failed').length,
            skipped: tests.filter((t) => results[t.id]?.status === 'skipped').length,
            running: tests.filter((t) => results[t.id]?.status === 'running').length,
          };

          const isExpanded = expandedGroups.has(group);

          return (
            <div key={group} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <span className="font-semibold text-slate-800">{group}</span>
                  <span className="text-xs text-slate-500">{tests.length} tests</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-xs">
                    {groupResultCounts.passed > 0 && (
                      <span className="text-emerald-600 font-medium">{groupResultCounts.passed} ✓</span>
                    )}
                    {groupResultCounts.failed > 0 && (
                      <span className="text-red-600 font-medium">{groupResultCounts.failed} ✕</span>
                    )}
                    {groupResultCounts.skipped > 0 && (
                      <span className="text-amber-600 font-medium">{groupResultCounts.skipped} ⊘</span>
                    )}
                    {groupResultCounts.running > 0 && (
                      <span className="text-blue-600 font-medium">{groupResultCounts.running} ◌</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      runGroup(group);
                    }}
                    disabled={isRunning}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 rounded-md transition-colors"
                  >
                    <Play className="w-3 h-3" />
                    Run
                  </button>
                </div>
              </button>

              {/* Group body */}
              {isExpanded && (
                <div className="border-t border-slate-100">
                  {visibleTests.map((test) => {
                    const result = results[test.id];
                    const status: TestStatus = result?.status || 'idle';
                    const config = statusConfig[status];
                    const isTestExpanded = expandedTests.has(test.id);

                    return (
                      <div
                        key={test.id}
                        className={`border-b border-slate-50 last:border-b-0 ${status === 'failed' ? 'bg-red-50/30' : ''}`}
                      >
                        <button
                          onClick={() => toggleTest(test.id)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors text-left"
                        >
                          <span className={config.color}>{config.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{test.name}</div>
                            {test.description && (
                              <div className="text-xs text-slate-500 truncate">{test.description}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {result?.durationMs && (
                              <span className="text-xs text-slate-400">{result.durationMs}ms</span>
                            )}
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                              {config.label}
                            </span>
                            {(test.requiresAuth || test.requiresAdmin || test.requiresTrainer) && (
                              <span className="text-xs text-slate-400" title="Requires authentication">
                                <ShieldAlert className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                        </button>

                        {/* Expanded details */}
                        {isTestExpanded && result?.error && (
                          <div className="px-4 pb-3 pl-11">
                            <div className="bg-white border border-red-200 rounded-lg p-3">
                              <div className="text-xs font-semibold text-red-700 mb-1">Error</div>
                              <pre className="text-xs text-red-600 whitespace-pre-wrap break-words font-mono">
                                {result.error}
                              </pre>
                            </div>
                          </div>
                        )}

                        {isTestExpanded && status === 'passed' && (
                          <div className="px-4 pb-3 pl-11">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                              <div className="text-xs font-semibold text-emerald-700 mb-1">Success</div>
                              <div className="text-xs text-emerald-600">API responded correctly in {result?.durationMs}ms</div>
                            </div>
                          </div>
                        )}

                        {isTestExpanded && status === 'skipped' && (
                          <div className="px-4 pb-3 pl-11">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <div className="text-xs font-semibold text-amber-700 mb-1">Skipped</div>
                              <div className="text-xs text-amber-600">{result?.error || test.skipReason || 'No reason given'}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
